import { merge } from 'lodash';
import React, { createContext, useContext, useMemo } from 'react';
import { grid } from '@shared/styles';
import { CardRenderContext, RenderContext } from './render';

export interface CardLayoutConfig {
  staticBlock: {
    paddingLeft: number;
    displayLabel: boolean;
  };
  listFooter: {
    paddingTop: number;
  };
  fieldFile: {
    paddingHorizontal: number;
  };
  blockIcon: {
    size: number;
  };
}

export const defaultConfig: CardLayoutConfig = {
  staticBlock: {
    paddingLeft: 3,
    displayLabel: true,
  },
  listFooter: {
    paddingTop: 0,
  },
  fieldFile: {
    paddingHorizontal: grid(3),
  },
  blockIcon: {
    size: 40,
  },
};

export const listConfig = merge({}, defaultConfig, {
  staticBlock: {
    paddingLeft: 2,
  },
  listFooter: {
    paddingTop: grid(1),
  },
  fieldFile: {
    paddingHorizontal: grid(1),
  },
  blockIcon: {
    size: 30,
  },
} as CardLayoutConfig);

export const taskListConfig = merge({}, defaultConfig, {
  staticBlock: {
    paddingLeft: 0,
  },
  listFooter: {
    paddingTop: grid(1),
  },
  fieldFile: {
    paddingHorizontal: grid(1),
  },
  blockIcon: {
    size: 30,
  },
} as CardLayoutConfig);

export const calendarConfig = merge({}, defaultConfig, {
  staticBlock: {
    paddingLeft: 1,
  },
  blockIcon: {
    size: 20,
  },
} as CardLayoutConfig);

export const agendaConfig = merge({}, defaultConfig, {
  staticBlock: {
    paddingLeft: 2,
  },
  blockIcon: {
    size: 30,
  },
} as CardLayoutConfig);

export const tableConfig = merge({}, defaultConfig, {
  staticBlock: {
    paddingLeft: 2,
    displayLabel: false,
  },
  listFooter: {
    paddingTop: grid(1.65),
  },
  fieldFile: {
    paddingHorizontal: grid(1),
  },
  blockIcon: {
    size: 30,
  },
} as CardLayoutConfig);

export const formConfig = defaultConfig;

export const detailConfig = defaultConfig;

export type CardLayout = Record<RenderContext, CardLayoutConfig>;

const configByRenderContext: Record<RenderContext, CardLayoutConfig> = {
  table: tableConfig,
  list: listConfig,
  taskList: taskListConfig,
  agenda: agendaConfig,
  calendar: calendarConfig,
  form: formConfig,
  detail: detailConfig,
};

export const CardLayoutConfigContext = createContext<CardLayout>(configByRenderContext);

export function useLayoutConfig() {
  const config = useContext(CardLayoutConfigContext);
  const renderContext = useContext(CardRenderContext);

  return useMemo(() => config[renderContext], [config, renderContext]);
}

export function CardLayoutConfigProvider({ children }) {
  return (
    <CardLayoutConfigContext.Provider value={configByRenderContext}>
      {children}
    </CardLayoutConfigContext.Provider>
  );
}
